import type { GenerationErrorReason } from '@tunasong/schemas'

const reasonOf = (data: unknown): unknown =>
  data && typeof data === 'object' ? (data as { context?: { reason?: unknown } }).context?.reason : undefined

/**
 * Why an AI generation request was refused, from the RTK error it rejected with. The API names the
 * reason in `context.reason` of its JSON body. Older deployments answer the quota refusal in plain
 * text, which RTK reports as a `PARSING_ERROR` with `originalStatus: 403`, so that is read too.
 */
export const getRefusalReason = (error: unknown): GenerationErrorReason | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const { status, originalStatus, data } = error as { status?: unknown; originalStatus?: unknown; data?: unknown }
  const reason = reasonOf(data)
  if (reason === 'quota-exceeded' || reason === 'no-profile') {
    return reason
  }
  const is403 = status === 403 || (status === 'PARSING_ERROR' && originalStatus === 403)
  if (is403 && typeof data === 'string' && /quota exceeded/i.test(data)) {
    return 'quota-exceeded'
  }
  return undefined
}

/**
 * What to tell the user when Tunabrain did not make `what` (e.g. "mood sketch"). Never the server's
 * text, and never "this month": the quota has no reset (docs/ai-language.md).
 */
export const generationFailedMessage = (what: string, reason: string | undefined) =>
  reason === 'quota-exceeded'
    ? `You've used all your AI tokens, so Tunabrain couldn't make the ${what}.`
    : `Tunabrain couldn't make the ${what}. Please try again.`
